import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { sipToXsipShift, getMandateStatus } from "../../../api/bseApi";

function formatDateForBse(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function isFutureDate(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d > today;
}

/**
 * SipToXsipShiftScreen - BSE StAR MF SIP to XSIP Shift
 * Opens from SIP Details or SIP List screen
 * Receives: ClientCode, SIP REGID, SchemeCd, List of approved XSIP Mandates
 */
export default function SipToXsipShiftScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const state = location.state;
  const [context, setContext] = useState(null);
  const [mandates, setMandates] = useState([]);
  const [form, setForm] = useState({
    xsipMandateId: "",
    effectiveDate: "",
  });
  const [loading, setLoading] = useState(false);
  const [fetchingMandates, setFetchingMandates] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const clientCode = state?.clientCode || params.get("clientCode");
    const regId = state?.regId || params.get("regId");
    const schemeCd = state?.schemeCd || params.get("schemeCd");
    const sipStatus = state?.sipStatus || params.get("sipStatus") || "active";
    const mandatesFromState = state?.mandates;

    if (clientCode && regId && schemeCd) {
      setContext({
        clientCode,
        regId,
        schemeCd,
        schemeName: state?.schemeName || params.get("schemeName") || "",
        sipStatus,
        mandates: mandatesFromState,
      });
      if (Array.isArray(mandatesFromState) && mandatesFromState.length > 0) {
        setMandates(mandatesFromState);
        setFetchingMandates(false);
      }
      return;
    }
    Swal.fire("Error", "Missing SIP details. Open from SIP List or SIP Details with ClientCode, REGID, SchemeCd.", "error").then(() => navigate("/user"));
  }, [location.state, location.search, navigate]);

  useEffect(() => {
    if (!context?.clientCode || (context?.mandates && context.mandates.length > 0)) return;
    const loadMandates = async () => {
      setFetchingMandates(true);
      try {
        const fromDate = "01/01/2020";
        const toDate = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "/");
        const res = await getMandateStatus({
          fromDate,
          toDate,
          clientCode: context.clientCode,
        });
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setMandates(res.data.data);
        } else if (res.data?.mandateDetails && Array.isArray(res.data.mandateDetails)) {
          setMandates(res.data.mandateDetails);
        }
      } catch (err) {
        console.warn("Mandate list load failed:", err.message);
      } finally {
        setFetchingMandates(false);
      }
    };
    loadMandates();
  }, [context?.clientCode, context?.mandates]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    if (!context?.regId) err.regId = "REGID must exist";
    if (!context?.schemeCd) err.schemeCd = "SchemeCd must exist";
    const status = (context?.sipStatus || "").toLowerCase();
    if (status !== "active") err.sipStatus = "SIP status must be Active";
    if (!form.xsipMandateId?.trim()) err.xsipMandateId = "XSIP Mandate selection is mandatory";
    if (!form.effectiveDate) err.effectiveDate = "Effective Date is mandatory";
    else if (!isFutureDate(form.effectiveDate)) err.effectiveDate = "Effective Date must be a future date";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const payload = {
        regId: context.regId,
        clientCode: context.clientCode,
        schemeCd: context.schemeCd,
        xsipMandateId: form.xsipMandateId.trim(),
        effectiveDate: formatDateForBse(form.effectiveDate),
      };

      const res = await sipToXsipShift(payload);

      if (res.data?.status === 1) {
        Swal.fire({
          icon: "success",
          title: "SIP Shifted to XSIP",
          text: "SIP successfully shifted to XSIP.",
        });
        navigate("/user");
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "SIP to XSIP Shift failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const msg =
        err.response?.data?.bseMessage ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "SIP to XSIP Shift failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const mandateList = mandates || [];
  const status = (context.sipStatus || "").toLowerCase();

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">SIP to XSIP Shift</li>
              </ol>
            </nav>
            <h5>SIP to XSIP Shift</h5>
            <p className="text-muted mb-0">
              Client Code: {context.clientCode} | Status: {context.sipStatus}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Shift Details
              </h6>
              {status !== "active" && (
                <div className="alert alert-warning mb-4">
                  SIP status must be Active to shift to XSIP. Current status: {context.sipStatus}
                </div>
              )}
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">SIP Registration ID (REGID)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={context.regId}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Scheme (SchemeCd)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={context.schemeName ? `${context.schemeName} (${context.schemeCd})` : context.schemeCd}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">XSIP Mandate (XSIPMANDATEID) <span className="text-danger">*</span></label>
                  {fetchingMandates ? (
                    <div className="form-control" style={{ backgroundColor: "#f8f9fa" }}>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Loading mandates...
                    </div>
                  ) : mandateList.length === 0 ? (
                    <div className="alert alert-warning mb-0">
                      No approved XSIP mandates found. Register an XSIP/e-NACH mandate first.
                    </div>
                  ) : (
                    <select
                      className={`form-select ${errors.xsipMandateId ? "is-invalid" : ""}`}
                      name="xsipMandateId"
                      value={form.xsipMandateId}
                      onChange={handleChange}
                    >
                      <option value="">Select XSIP Mandate</option>
                      {mandateList.map((m, i) => {
                        const mid = m.MandateId || m.mandateId || m.MandateID || m.id || String(i + 1);
                        const mlabel = m.MandateType || m.MandateId || mid || `Mandate ${i + 1}`;
                        return (
                          <option key={i} value={mid}>
                            {mlabel}
                          </option>
                        );
                      })}
                    </select>
                  )}
                  {errors.xsipMandateId && <div className="invalid-feedback">{errors.xsipMandateId}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Effective Date (DD/MM/YYYY) <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-control ${errors.effectiveDate ? "is-invalid" : ""}`}
                    name="effectiveDate"
                    value={form.effectiveDate}
                    onChange={handleChange}
                  />
                  {errors.effectiveDate && <div className="invalid-feedback">{errors.effectiveDate}</div>}
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || status !== "active" || mandateList.length === 0}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Shifting...
                    </>
                  ) : (
                    "Shift to XSIP"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
